import { AdminCouponResponse, Coupon, Organisation } from '@avanzu/types';
import LocalOfferTwoTone from '@mui/icons-material/LocalOfferTwoTone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BreadCrumb from '../../../components/BreadCrumb';
import OrganisationChip from '../../../components/OrganisationChip';
import { apiClient } from '../../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../../utils/formatDate';
import { getSubscriptionStatusColor } from '../../../utils/getSubscriptionStatusColor';

export default function CouponView() {
  const { couponId } = useParams<{ couponId: string }>();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedPromoCode, setSelectedPromoCode] = useState<string>('all');

  useEffect(() => {
    if (couponId) {
      fetchCoupon();
    }
  }, [couponId]);

  const fetchCoupon = async () => {
    if (!couponId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/coupons/${couponId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch coupon details');
      }

      const data: AdminCouponResponse = await response.json();
      setCoupon(data.coupon);
      setOrganisations(data.organisations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching coupon:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async (code: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const filteredOrganisations = useMemo(() => {
    if (selectedPromoCode === 'all') {
      return organisations;
    }
    return organisations.filter((org) => org.promotionCode === selectedPromoCode);
  }, [organisations, selectedPromoCode]);

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.percentOff) {
      return `${coupon.percentOff}% off`;
    }
    if (coupon.amountOff && coupon.currency) {
      return `${coupon.amountOff.toFixed(2)} ${coupon.currency.toUpperCase()} off`;
    }
    return 'N/A';
  };

  const formatDuration = (coupon: Coupon) => {
    if (coupon.duration === 'forever') {
      return 'Forever';
    }
    if (coupon.duration === 'once') {
      return 'Once';
    }
    if (coupon.duration === 'repeating' && coupon.durationInMonths) {
      return `${coupon.durationInMonths} month${coupon.durationInMonths > 1 ? 's' : ''}`;
    }
    return coupon.duration;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !coupon) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <BreadCrumb />
          <Typography variant="h5" color="error">
            {error || 'Coupon not found'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <BreadCrumb customLabel={coupon.name || coupon.id} />

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'success.main' }}>
            <LocalOfferTwoTone sx={{ fontSize: 32, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {coupon.name || coupon.id}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {coupon.id}
              </Typography>
              <Link
                href={`https://dashboard.stripe.com/coupons/${coupon.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
              >
                View in Stripe
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            </Box>
          </Box>
        </Box>
        <Chip
          label={coupon.valid ? 'Valid' : 'Invalid'}
          color={coupon.valid ? 'success' : 'default'}
          sx={{ height: 32, fontSize: '0.875rem' }}
        />
      </Box>

      {/* Promotion Codes Card */}
      {coupon.promotionCodes.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Promotion Codes
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {coupon.promotionCodes.map((promoCode, idx) => (
                <Chip
                  key={idx}
                  label={
                    copiedCode === promoCode.code
                      ? '✓ Copied!'
                      : `${promoCode.code} (${promoCode.attributions})`
                  }
                  color={copiedCode === promoCode.code ? 'success' : 'primary'}
                  onClick={(e) => handleCopyCode(promoCode.code, e)}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    height: 36,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Click to copy • Numbers in parentheses show attribution count
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Coupon Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Coupon Details
          </Typography>
          <Grid container spacing={4}>
            {/* Discount Information */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Discount
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Discount Value" value={formatDiscount(coupon)} />
                <InfoItem label="Duration" value={formatDuration(coupon)} />
                {coupon.redeemBy && (
                  <InfoItem
                    label="Redeem By"
                    value={formatDate(coupon.redeemBy, DateFormatOptions.DATEONLY)}
                  />
                )}
              </Stack>
            </Grid>

            {/* Usage Statistics */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Usage
              </Typography>
              <Stack spacing={2}>
                <InfoItem label="Times Redeemed" value={coupon.timesRedeemed} />
                <InfoItem
                  label="Max Redemptions"
                  value={coupon.maxRedemptions || 'Unlimited'}
                />
                <InfoItem
                  label="Remaining"
                  value={
                    coupon.maxRedemptions
                      ? coupon.maxRedemptions - coupon.timesRedeemed
                      : 'Unlimited'
                  }
                />
              </Stack>
            </Grid>

            {/* Metadata */}
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Metadata
              </Typography>
              <Stack spacing={2}>
                <InfoItem
                  label="Created"
                  value={formatDate(coupon.created, DateFormatOptions.DATEANDTIME)}
                />
                <InfoItem
                  label="Status"
                  value={
                    <Chip
                      label={coupon.valid ? 'Valid' : 'Invalid'}
                      color={coupon.valid ? 'success' : 'default'}
                      size="small"
                    />
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Organisations Using This Coupon */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Organisations ({filteredOrganisations.length}
              {selectedPromoCode !== 'all' && ` of ${organisations.length}`})
            </Typography>
            {coupon && coupon.promotionCodes.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Promo Code</InputLabel>
                <Select
                  value={selectedPromoCode}
                  label="Filter by Promo Code"
                  onChange={(e) => setSelectedPromoCode(e.target.value)}
                >
                  <MenuItem value="all">All Codes</MenuItem>
                  {coupon.promotionCodes.map((promoCode) => (
                    <MenuItem key={promoCode.code} value={promoCode.code}>
                      {promoCode.code} ({promoCode.attributions})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          {organisations.length === 0 ? (
            <Typography color="text.secondary">
              No organisations have redeemed this coupon yet
            </Typography>
          ) : filteredOrganisations.length === 0 ? (
            <Typography color="text.secondary">
              No organisations found for the selected promotion code
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Legal Name</TableCell>
                    <TableCell>Promotion Code Used</TableCell>
                    <TableCell>Subscription Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrganisations.map((org) => (
                    <TableRow
                      key={org.id}
                      onClick={() => navigate(`/app/organisations/${org.id}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell>
                        <OrganisationChip organisation={org} clickable={false} />
                      </TableCell>
                      <TableCell>{org.legalName}</TableCell>
                      <TableCell>
                        {org.promotionCode ? (
                          <Chip
                            label={org.promotionCode}
                            color="primary"
                            size="small"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={org.subscriptionStatus}
                          color={getSubscriptionStatusColor(org.subscriptionStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(org.createdAt, DateFormatOptions.DATEONLY)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
